
import { Component, Prop, Vue } from 'vue-property-decorator';

import { sharedState } from '../../../../../framework/state';
import { getResource } from '../../../../../framework/client/resource';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import { FrameworkUserRole } from '../../../../../../common/framework/enumeration/FrameworkUserRole';

import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { VehicleType } from '../../../../../../common/application/model/enums/VehicleType';
import { VehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { VehicleIntendedUse } from '../../../../../../common/application/model/enums/VehicleIntendedUse';
import { VehicleUsageType } from '../../../../../../common/application/model/enums/VehicleUsageType';
import { VehicleEnginePowerType } from '../../../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleElectricalClass } from '../../../../../../common/application/model/enums/VehicleElectricalClass';
import { FinancingContractType } from '../../../../../../common/application/model/enums/FinancingContractType';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { Account } from '../../../../../../common/application/model/Account';

import VendorContracts from '../vendor/VendorContracts.vue';
import VehicleContractComments from './comment/VehicleContractComments.vue';
import VehicleContractPurchaseInvoices from '../../invoice/purchase/VehicleContractPurchaseInvoices.vue';
import VehicleUsageReports from '../../../manager/report/VehicleUsageReports.vue';
import ViewDocuments from '../../document/ViewDocuments.vue';
import AddDocument from '../../document/AddDocument.vue';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';

import { getFinancingContractIdByContractNumber } from '../../../../client/vehicle_contract_client';
import { getCustomerOptions, getVendorOptions } from '../../../../service/application_options_service';
import { formatCurrency, formatNumber, formatToFinnishDate } from '../../../../service/helper_service';

@Component({
    components: {
        VendorContracts,
        VehicleContractComments,
        VehicleContractPurchaseInvoices,
        VehicleUsageReports,
        ViewDocuments,
        AddDocument,
    },
    beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: ViewVehicleContract) => {
            vm.getPrevRouter(from); //get the previous URL
        });
    },
})
export default class ViewVehicleContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as VehicleContract,
        customer: '',
        vehicleState: '',
        vehicleType: '',
        vehicleEnginePowerType: '',
        vehicleElectricalClass: '',
        vehicleIntendedUse: '',
        vehicleUsageType: '',
        smartFleetContractType: '',
        financingContractType: '',
        financingContractFinancingVendor: '',
        financingContractRepurchaseVendor: '',
        customerOptions: [] as { id: string | undefined; label: string }[],
        vehicleTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleStateOptions: [] as { id: string | undefined; label: string }[],
        vehicleIntendedUseOptions: [] as { id: string | undefined; label: string }[],
        vehicleUsageTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleEnginePowerTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleElectricalClassOptions: [] as { id: string | undefined; label: string }[],
        financingContractTypeOptions: [] as { id: string | undefined; label: string }[],
        smartFleetContractTypeOptions: [] as { id: string | undefined; label: string }[],
        financingContractCustomerExpenseAccount: {} as Account,
        smartFleetContractCustomerExpenseAccount: {} as Account,
        vendorOptions: [] as { id: string | undefined; label: string }[],
        showAllVehicleFields: false,
        showAllSmartFleetFields: false,
        showAllFinancingFields: false,
        financingContractId: '',
        loading: true,
        hasVehicleTracking: false,
        totalComments: 0,
        activeTab: 0,
        prevRoute: '',
        showAddDocument: false,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        this.local.activeTab = Number(localStorage.getItem(`${this.resourceType}_ActiveTab`));
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorOptions = await getVendorOptions();

        this.local.vehicleTypeOptions = getEnumerationOptions('VehicleType', Object.keys(VehicleType), this);
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.vehicleIntendedUseOptions = getEnumerationOptions(
            'VehicleIntendedUse',
            Object.keys(VehicleIntendedUse),
            this,
        );
        this.local.vehicleUsageTypeOptions = getEnumerationOptions(
            'VehicleUsageType',
            Object.keys(VehicleUsageType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );
        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );

        this.local.row = (await getResource<VehicleContract>(this.resourceType, this.id))!!;

        this.local.customer = getOptionLabel(this.local.row.customerId, this.local.customerOptions);
        this.local.vehicleState = getOptionLabel(this.local.row.vehicleState, this.local.vehicleStateOptions);
        this.local.vehicleType = getOptionLabel(this.local.row.vehicleType, this.local.vehicleTypeOptions);
        this.local.vehicleEnginePowerType = getOptionLabel(
            this.local.row.vehicleEnginePowerType,
            this.local.vehicleEnginePowerTypeOptions,
        );
        this.local.vehicleElectricalClass = getOptionLabel(
            this.local.row.vehicleElectricalClass,
            this.local.vehicleElectricalClassOptions,
        );
        this.local.vehicleIntendedUse = getOptionLabel(
            this.local.row.vehicleIntendedUse,
            this.local.vehicleIntendedUseOptions,
        );
        this.local.vehicleUsageType = getOptionLabel(
            this.local.row.vehicleUsageType,
            this.local.vehicleUsageTypeOptions,
        );
        this.local.smartFleetContractType = getOptionLabel(
            this.local.row.smartFleetContractType,
            this.local.smartFleetContractTypeOptions,
        );
        this.local.financingContractType = getOptionLabel(
            this.local.row.financingContractType,
            this.local.financingContractTypeOptions,
        );
        this.local.financingContractFinancingVendor = getOptionLabel(
            this.local.row.financingContractFinancingVendorId || '',
            this.local.vendorOptions,
        );
        this.local.financingContractRepurchaseVendor = getOptionLabel(
            this.local.row.financingContractRepurchaseVendorId || '',
            this.local.vendorOptions,
        );

        if (this.local.row.financingContractContractNumber) {
            const financingContractIds = (await getFinancingContractIdByContractNumber(
                this.local.row.financingContractContractNumber,
            ))!!;
            this.local.financingContractId = financingContractIds[0].id;
        }
        localStorage.removeItem(`${this.resourceType}_ActiveTab`);

        if (this.local.row.financingContractCustomerExpenseAccountId) {
            this.local.financingContractCustomerExpenseAccount = (await getResource<Account>(
                ApplicationResource.ACCOUNT,
                this.local.row.financingContractCustomerExpenseAccountId,
            ))!!;
        }

        if (this.local.row.smartFleetContractCustomerExpenseAccountId) {
            this.local.smartFleetContractCustomerExpenseAccount = (await getResource<Account>(
                ApplicationResource.ACCOUNT,
                this.local.row.smartFleetContractCustomerExpenseAccountId,
            ))!!;
        }

        this.local.loading = false;
    }

    back() {
        this.$router.go(-1);
    }

    edit() {
        this.$router.push(`/vehicle-contract/${this.local.row.id}/edit`);
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    addDocument() {
        if (this.isManager) {
            this.local.showAddDocument = true;
        } else if (this.isAdmin) {
            this.$router.push({ name: 'EditVehicleContract', params: { activeTab: this.local.activeTab } } as any);
        }
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 3;
        }
    }

    getActiveTab(value: number) {
        localStorage.setItem(`${this.resourceType}_ActiveTab`, JSON.stringify(value));
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    formatDate(date: Date | string): string {
        if (this.local.loading || !date) {
            return '-';
        }

        return formatToFinnishDate(date.toString());
    }

    formatNumberValue(value: number): string {
        return formatNumber(value);
    }
}
